// ABASphere is intentionally split across several shared chunks. A browser can
// negatively cache every parallel request during an outage, so the bounded
// repair budget must cover the complete route graph rather than only its entry.
const MAX_CHUNK_RECOVERY_ATTEMPTS = 32;
const SAFE_CHUNK_PATH = /^\/static\/(?:js\/[A-Za-z0-9._-]+\.js|css\/[A-Za-z0-9._-]+\.css)$/;

export const chunkRequestFromError = error => {
  const explicitRequest = String(error?.request || '').trim();
  if (explicitRequest) return explicitRequest;

  const message = String(error?.message || error || '');
  const requestMatch = message.match(/((?:https?:\/\/[^\s)]+)?\/static\/(?:js|css)\/[^\s)]+)/i);
  return requestMatch?.[1] || '';
};

export const isLazyChunkLoadError = error => {
  const detail = `${String(error?.name || '')} ${String(error?.message || error || '')}`;
  return /(?:ChunkLoadError|Loading (?:CSS )?chunk [^\s]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module)/i.test(detail);
};

export const cacheBustedChunkUrl = (request, {
  location = typeof window !== 'undefined' ? window.location : null,
  token = `${Date.now()}-${Math.random().toString(36).slice(2)}`,
} = {}) => {
  if (!request || !location?.href || !location?.origin) return '';
  try {
    const url = new URL(request, location.href);
    if (url.origin !== location.origin || !SAFE_CHUNK_PATH.test(url.pathname)) return '';
    url.searchParams.set('recon-chunk-recovery', String(token));
    return url.href;
  } catch {
    return '';
  }
};

export const loadCacheBustedChunk = url => new Promise((resolve, reject) => {
  if (typeof document === 'undefined' || !document.head) {
    reject(new Error('The application cannot recover a bundle outside the browser.'));
    return;
  }

  const parsed = new URL(url, window.location.href);
  const isStylesheet = parsed.pathname.endsWith('.css');
  const element = document.createElement(isStylesheet ? 'link' : 'script');
  let settled = false;
  const finish = error => {
    if (settled) return;
    settled = true;
    element.onload = null;
    element.onerror = null;
    element.remove();
    if (error) reject(error);
    else resolve();
  };

  if (isStylesheet) {
    element.rel = 'stylesheet';
    element.href = url;
  } else {
    element.async = true;
    element.charset = 'utf-8';
    element.src = url;
  }
  element.onload = () => finish();
  element.onerror = () => finish(new Error('The application bundle recovery request failed.'));
  document.head.appendChild(element);
});

export const importWithChunkRecovery = async (importer, {
  loadChunk = loadCacheBustedChunk,
  location = typeof window !== 'undefined' ? window.location : null,
  maxAttempts = MAX_CHUNK_RECOVERY_ATTEMPTS,
  tokenFactory = attempt => `${Date.now()}-${attempt}-${Math.random().toString(36).slice(2)}`,
} = {}) => {
  const attemptedRequests = new Set();
  let lastError;

  for (let attempt = 0; attempt <= maxAttempts; attempt += 1) {
    try {
      return await importer();
    } catch (error) {
      lastError = error;
      if (!isLazyChunkLoadError(error) || attempt === maxAttempts) throw error;

      const request = chunkRequestFromError(error);
      const recoveryUrl = cacheBustedChunkUrl(request, {
        location,
        token: tokenFactory(attempt),
      });
      if (!recoveryUrl) throw error;

      const requestKey = new URL(recoveryUrl).pathname.toLowerCase();
      if (attemptedRequests.has(requestKey)) throw error;
      attemptedRequests.add(requestKey);
      await loadChunk(recoveryUrl);
    }
  }

  throw lastError;
};

export { MAX_CHUNK_RECOVERY_ATTEMPTS };
